import store from 'redux/store';
import {logout} from 'redux/store/auth';
import {toast} from 'react-toastify';
import {getPushSubscription} from 'utils/adminPush';

/**
 * Force-logs the user out the moment their session token is no longer valid
 * (expired or rejected by the backend), so they are sent back to the login
 * screen instead of silently hitting failing requests on a stale tab.
 *
 * The presence of the token in localStorage acts as the guard: `logout()`
 * removes it synchronously, so any other requests that fail with 401 in the
 * same moment become no-ops here (no duplicate toasts / dispatches), and a
 * fresh login naturally re-arms this handler for the next expiry.
 *
 * Dispatching `logout()` flips `auth.isAuthenticated` to false, which makes the
 * route guard in App.js unmount the dashboard and redirect to the login page.
 */
export const handleSessionExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    return;
  }

  // Tell the user *why* they are being kicked out before we force the logout,
  // so they aren't left wondering why the screen changed. The ToastContainer
  // lives at the App root (outside the router), so this notification stays
  // visible through the redirect to the login screen.
  toast.error(
    'Your session has expired for security reasons. You have been logged out — please log in again to continue.',
    {autoClose: 6000},
  );

  // The token is already dead, so the server can't be told: drop the browser
  // subscription instead, which stops this agent's alerts landing here.
  getPushSubscription().then(sub => sub?.unsubscribe().catch(() => {}));

  store.dispatch(logout());
};
